import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {OptimizationCardScratchpadState} from '../../scratchpad/optimizationCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import supportsAttributionSignals from '../../utils/optimization/supportsAttributionSignals';

const setAttributionSignalsInputSchema = z.object({
  signalIds: z
    .array(z.number())
    .optional()
    .nullable()
    .describe('Array of attribution signal IDs. If not provided or empty, all signals are selected.'),
  selectAll: z
    .boolean()
    .optional()
    .nullable()
    .describe('If true, select all attribution signals.'),
});

function errorResult(message: string) {
  return toolResult(JSON.stringify({status: 'error', message}));
}

export default function setAttributionSignalsTool() {
  async function execute(
    params: ToolExecuteParams<
      typeof setAttributionSignalsInputSchema,
      OptimizationCardScratchpadState
    >,
  ) {
    const {signalIds, selectAll} = params.input;
    const validatedScratchpad = validateAgentScratchpad<OptimizationCardScratchpadState>(
      params.toolUtils.agentScratchpad,
      NodeType.GENERAL_OPTIMIZATION,
    );

    if (!validatedScratchpad.card.configuration) {
      return errorResult('Optimization configuration not found');
    }

    const {configuration} = validatedScratchpad.card;

    // Check if current optimizeBy supports attribution signals
    if (!supportsAttributionSignals(configuration.optimizeBy)) {
      return errorResult(
        'Attribution signals can only be set when optimizing by Conversions or Click Through Conversions',
      );
    }

    // If selectAll is explicitly true or signalIds is empty/null
    if (selectAll === true || !signalIds || signalIds.length === 0) {
      configuration.areAllAttributionSignalsSelected = true;
      configuration.attributionSignalIds = [];

      return toolResult('All attribution signals selected');
    }

    // Set specific signal IDs
    configuration.areAllAttributionSignalsSelected = false;
    configuration.attributionSignalIds = signalIds;

    return toolResult(`Attribution signals set: ${signalIds.length} signal(s) selected`);
  }

  return tool<typeof setAttributionSignalsInputSchema, OptimizationCardScratchpadState>({
    name: V2CardAgentToolName.SetAttributionSignals,
    displayName: 'Setting attribution signals',
    description:
      'Set attribution signals for conversion optimization. Only available when optimizing by Conversions or Click Through Conversions.',
    inputSchema: setAttributionSignalsInputSchema,
    execute,
  });
}
